import type {RecurringEvent} from '@Components/event-wizard/recurring-event-selector/recurring-event-selector.types';
import type {EventWizardVenue} from '@Components/event-wizard/event-wizard-venue/event-wizard-venue.types';
import type {EventType, EventTypes} from '@Components/event-wizard/type-dropdown/type-dropdown.types';
import type {TimeZone} from 'timezones-list';
import type {CoverPhoto} from '@Components/event-wizard/event-wizard-image-video/event-wizard-image-video.types';
import type {EventStyles} from '@Components/event-wizard/event-style/event-style.types';
import type {EventWizardOrganisers, OrganiserDetails} from '@Components/event-wizard/event-wizard-organisers/event-wizard-organisers.types';
import type {PlaceDetails} from '@Components/google-map/google-map.types';
import type {EventRegistrationFields, EventVO, EventVOMinifiedWizardPrefill} from '@Components/events/events.types';
import type {DateTimeMobilePanel} from '@Panels/event-wizard-date-time-panel/event-wizard-date-time-panel.types';
import type {Ref} from "react";
import type {EmailVerificationRequiredData} from '@Panels/user-email-verification-panel/user-email-verification-panel.types';
import type {TicketingConfig, TicketingFeeParams} from '@Components/event-wizard/event-wizard-ticketing/event-wizard-ticketing.types';

export interface EventWizardProps {
  googleMapAPIKey: string;
  organisers: OrganiserDetails[];
  types: EventType[];
  teamName: string | null;
  customRegisterBtnTexts: string[];
  teamSize: number | null;
  userPastVenues: PlaceDetails[];
  eventWizardSourceType: EventWizardSourceType;
  eventVO?: EventVO;
  eventVOMinifiedWizardPrefill?: EventVOMinifiedWizardPrefill;
  doesPAYGUserHaveAITemplateLimit: boolean;
  shouldUpsellRecurring: boolean;
  shouldShowRecurringEventDetails: boolean;
  isTicketingCountrySupported: boolean;
  isPaidTicketingFeatureAvailable: boolean;
  notifyMeEmail: string;
}

export enum EventWizardSourceType {
  DEFAULT = 0,
  DUPLICATE = 1,
  UPDATE = 2,
  PREFILL_FROM_DESIGN = 3,
  PREFILL_FROM_DATE_SELECTION = 4,
}

export interface EventWizardReduxState {
  eventHID?: string;
  googleMapAPIKey: string;
  title: string;
  types: EventTypes;
  description: string;
  isAIDescriptionGenerating: boolean;
  startDateTime: Date; // This is in the exact format the user has entered the date/time in. It is not converted to any timezone. Refer to PHP function: convertTimeToUTC
  endDateTime: Date; // This is in the exact format the user has entered the date/time in. It is not converted to any timezone. Refer to PHP function: convertTimeToUTC
  timeZone: TimeZone;
  recurringEvent: RecurringEvent;
  showCountdown: boolean;
  venue: EventWizardVenue;
  userPastVenues: PlaceDetails[];
  registrationDetails: EventRegistrationFields;
  ticketingConfig: TicketingConfig;
  ticketingFees: TicketingFeeParams | null; // null until lazily fetched on the Tickets step
  selectedRegistrationMode: RegistrationMode; // The active step-2 tile; explicit so it survives remount and distinguishes Custom Link (URL pending) from RSVP.
  isRemoveWatermark: boolean;
  isSharedInTeam: boolean;
  isSharedAsGoogleEvent: boolean;
  organisers: EventWizardOrganisers;
  coverPhoto: CoverPhoto;
  shouldCheckIfInErrState: boolean;
  styles: EventStyles;
  teamName?: string;
  currentProgressStep: EventWizardProgressStep;
  isEventBeingCreated: boolean;
  shouldNextBtnBeDisabled: boolean;
  shouldUpdateExpandedReviewSection: boolean;
  dateTimeMobilePanel: DateTimeMobilePanel;
  pageSourceType: EventWizardSourceType;
  isDesignPrefillContentLoading: boolean;
  progressStepLandedOn: EventWizardProgressStep; // When landing on the wizard, we can land on a specific progress step
  isDescriptionUserMade: boolean;
  doesPAYGUserHaveAITemplateLimit: boolean;
  isStylesPanelOpen: boolean;
  shouldUpsellRecurring: boolean;
  shouldShowRecurringEventDetails: boolean;
  isTicketingCountrySupported: boolean;
  isPaidTicketingFeatureAvailable: boolean;
  notifyMeEmail: string;
}

export interface EventWizardContainerQuery {
  isXXsmallScreen?: boolean;
  isExtraSmallScreen?: boolean;
  isSmallScreen?: boolean;
  isMediumScreen?: boolean;
  isLargeScreen?: boolean;
  isExtraLargeScreen?: boolean;
}

export interface ContainerQueryParams {
  containerQueryParamProps: EventWizardContainerQuery;
  containerRef: Ref<HTMLDivElement>;
  hasContainerQueryInitialized: boolean;
}

export interface EventSaveErrorProps {
  message: string;
  data: EmailVerificationRequiredData;
}

export enum EventWizardProgressStep {
  OVERVIEW = 'OVERVIEW',
  TICKETING = 'TICKETING',
  REVIEW = 'REVIEW',
}

export enum RegistrationMode {
  RSVP = 'RSVP',
  TICKETS = 'TICKETS',
  CUSTOM_LINK = 'CUSTOM_LINK',
  NONE = 'NONE',
}

/**
 * The wizard state implied by a registration mode under a given recurrence. `mode` is the *resolved* mode —
 * Tickets is downgraded to None on a recurring event, so state can never hold a mode that cannot exist.
 */
export interface RegistrationModeState {
  mode: RegistrationMode;
  isSelected: boolean;
  isRsvp: boolean;
  areRegistrationsRequired: boolean;
}

export interface RegistrationModeTileDescriptor {
  mode: RegistrationMode;
  icon: string;
  title: string;
  description: string;
}

export const getRegistrationModeTiles = (): RegistrationModeTileDescriptor[] => {
  return [
    {
      mode: RegistrationMode.RSVP,
      icon: 'icon-user',
      title: window.i18next.t('pmwjs_event_registration_tile_rsvp_title'),
      description: window.i18next.t('pmwjs_event_registration_tile_rsvp_subtitle'),
    },
    {
      mode: RegistrationMode.TICKETS,
      icon: 'icon-events-schedule',
      title: window.i18next.t('pmwjs_event_ticketing_tile_tickets_title'),
      description: window.i18next.t('pmwjs_event_ticketing_tile_tickets_subtitle'),
    },
    {
      mode: RegistrationMode.CUSTOM_LINK,
      icon: 'icon-external-link',
      title: window.i18next.t('pmwjs_event_registration_tile_custom_link_title'),
      description: window.i18next.t('pmwjs_event_registration_tile_custom_link_subtitle'),
    },
    {
      mode: RegistrationMode.NONE,
      icon: 'icon-ban',
      title: window.i18next.t('pmwjs_event_registration_tile_none_title'),
      description: window.i18next.t('pmwjs_event_registration_tile_none_subtitle'),
    },
  ];
};

export enum EventCreationErrorType {
  NUM_CREATION_LIMIT = 'creation_limit',
  USER_EMAIL_VERIFICATION_REQUIRED = 'user_email_verification_required',
  RACE_CONDITION = 'race_condition',
  // Mirrors the backend EventSaveErrorType case of the same value; the two enums must stay in sync.
  RECURRING_PAID_TICKETING_NOT_SUPPORTED = 'recurring_paid_ticketing_not_supported',
  UNKNOWN = 'unknown',
}

export const EVENT_ERROR_LOG_TAG = 'event_feature';

export const LOADING_TOAST_KEY = 'WizardLoading';
export const MESSAGE_GROWL_KEY = 'MessageGrowl';

export const orderedProgressSteps = [EventWizardProgressStep.OVERVIEW, EventWizardProgressStep.TICKETING, EventWizardProgressStep.REVIEW];

export const PROGRESS_STEP_LABEL_KEYS: Record<EventWizardProgressStep, string> = {
  [EventWizardProgressStep.OVERVIEW]: 'pmwjs_overview',
  [EventWizardProgressStep.TICKETING]: 'pmwjs_event_wizard_tickets_and_registration',
  [EventWizardProgressStep.REVIEW]: 'pmwjs_details',
};
